/* ───────────────────────────────────────────────────────────────────────────
   Every word on the marketing site lives here, so copy can be edited without
   touching layout.

   THE SPINE, as the founder framed it on 18 Oct 2026:
     · The walkthrough is the movie. OpenSky is the theatre. THIS IS INTERNAL.
       It organises the page; it must never appear in the copy. "Film",
       "theatre", "scenes" and "screening" are all banned words on the site —
       the founder caught them leaking in on 19 Oct.
     · Skywalk, Route View, Meta Model and Digital Twin are SCENES of one
       film — not four products. Never list them under "Products".
     · The platform features exist so the movie gets watched. Eleven today,
       and the list grows.
     · The film takes the project's name. We are the venue.

   RULES
     1. Headlines stay short — mono furniture and wide crops punish length.
     2. Nothing here may claim a capability that is not live in production.
        Anything unconfirmed is marked TODO(hari).
     3. No pricing on this page. Metering belongs on a pricing page:
        inventory per unit, embed per project, a fee for CRM delivery, guided
        per minute of hosting, and the build itself priced by scope.
   ──────────────────────────────────────────────────────────────────────── */

/* THE LOGO WALL — the five builders, in grayscale until you hover or tap.
   Logos came from the company-profile page in the engagements repo
   (site/assets/client-logo-*). The grayscale-until-hover treatment is the
   standard SaaS logo wall: it stops five different brand palettes from
   fighting the page, and lets each one come forward when someone looks at it.

   TODO(hari): confirm each builder is happy to be named publicly before this
   leaves staging, and send better artwork if they have it — Beforest is 1920px
   wide and Tridasa is nearly square, so they do not sit on one baseline. */
export const clients = [
  { name: "Ruchira Projects", logo: "/images/brand/clients/ruchira.png", h: 26 },
  { name: "Tridasa Rise", logo: "/images/brand/clients/tridasa.png", h: 40 },
  { name: "Ankura Homes", logo: "/images/brand/clients/ankura.svg", h: 30 },
  { name: "Indiabulls", logo: "/images/brand/clients/indiabulls.png", h: 30 },
  { name: "Beforest", logo: "/images/brand/clients/beforest.png", h: 20 },
];

export const BRAND = "OpenSkyVR";
export const TAGLINE = "Property, experienced.";

export const home = {
  eyebrow: "For builders and their partners",
  headline: "Experience it before it exists.",
  /* Buyer action first, builder payoff second. The old line was two sentences
     doing three jobs and ended on a defensive note ("makes sure it actually
     gets watched"), which reads like answering an objection nobody raised. */
  lede: "A buyer opens a link and walks your whole project — their own phone, no app, no headset. You see every room they stopped in, and which agent sent them.",
  primary: "Talk to us",
  secondary: "See how it works",
  /* The hero, generated in Recraft from the founder's own brief (19 Oct) and
     recoloured into our palette by scratchpad/recolour.py — 47 source colours
     mapped onto the brand ramp by luminance, so the drawing's light survives
     and only the hue changes.

     It carries both halves at once: a buyer in a headset seeing the FINISHED
     tower while only its base is scaffolded (the movie), surrounded by the
     platform (the theatre) — a live call, the inventory grid, the AI
     presenter, the analytics. */
  image: "/images/brand/hero-vr.svg?v=3",

  /* Labels for the four capabilities floating around the tower. They are HTML,
     not SVG text, so they set in OpenSky Mono and stay editable here. The
     coordinates are percentages of the illustration's own 1280x832 box. */
  heroLabels: [
    { t: "Guided tour", x: 40.7, y: 21.2 },
    { t: "Live inventory", x: 40.2, y: 51.0 },
    { t: "AI presenter", x: 81.5, y: 19.5 },
    { t: "Dwell analytics", x: 82.3, y: 57.3 },
  ],
};

/* ── the movie ───────────────────────────────────────────────────────────
   One experience per project, carrying the project's own name. The four
   scenes are chapters of it. Definitions are the founder's, clarified
   2026-06-03 — do not re-derive them from the app's older EXPLAINERS copy. */
/* The four drawings came out of Recraft as bold minimal icons and were
   recoloured onto the brand palette (scratchpad/rc2). They were drawn at
   different optical weights — the drone is tiny, the headset huge — so each
   carries its own `scale` to make the row read as a set. */
export const film = {
  eyebrow: "The walkthrough",
  /* "Unified Experience" is the product's own name for the integration layer —
     all four sitting inside one portal a buyer never has to think about. */
  headline: "One walkthrough. One unified experience.",
  said: "— and it carries your project's name, not ours.",
  body: "A buyer never opens four things. They open one link and move through the project the way they would on a Sunday — approaching it, driving in, walking the rooms, standing in the finished thing.",
  scenes: [
    {
      k: "Skywalk",
      art: "/images/brand/parts/skywalk.svg?v=3",
      scale: 1,
      t: "The approach.",
      d: "Drone aerials flown in toward the project from the landmarks around it — the tech park, the hospital, the road they already drive. A buyer understands where it sits before they understand what it is.",
    },
    {
      k: "Route View",
      art: "/images/brand/parts/route-view.svg?v=3",
      scale: 1,
      t: "The drive in.",
      d: "Ground-level 360° capture along the real roads to your gate. It answers the commute objection with footage instead of a claim.",
    },
    {
      k: "Meta Model",
      art: "/images/brand/parts/meta-model.svg?v=3",
      scale: 1,
      t: "Inside, before it is built.",
      d: "The spaces that decide a sale, rendered as they will exist after handover — so a buyer compares a mid floor against a premium one without a site visit.",
    },
    {
      k: "Digital Twin",
      art: "/images/brand/parts/digital-twin.svg?v=3",
      scale: 1,
      t: "The thing itself.",
      d: "Once it stands, a true-to-life capture of the built space. Same link, now showing what is actually there. It survives into resale.",
    },
  ],
};

/* ── what we make films of ───────────────────────────────────────────── */
/* The video belongs INSIDE the walkthrough section, not in a band of its own.
   It is not a second offer — it is the other thing that comes out of the same
   capture visit, which is also the commercial argument for the build fee. */
export const video = {
  headline: "And the video, from the same capture.",
  body: "One visit produces both. The walkthrough a buyer explores at their own pace, and a short video cut from the same footage — the approach, the amenities, a unit — for the campaign, the hoarding screen and the site office.",
  /* Ankura's experience film, the same cut that runs on the company profile
     page in the engagements repo. preload="metadata" so the 16MB only
     downloads when someone actually presses play. */
  /* Served from the engagements site, which already hosts this cut publicly.
     Keeps a 16MB binary out of the app repo and off every Netlify build. */
  src: "https://engagements.openskyvr.com/assets/ankura-experience-film.mp4",
  poster: "/images/brand/ankura-film-poster.jpg",
};

export const projectTypes = {
  eyebrow: "What we cover",
  headline: "Five kinds of project.",
  body: "The method changes with the asset. A plotted layout has to be understood from above; a tower has to be compared floor to floor; an estate has to be felt at scale.",
  /* Drawn in Recraft as one consistent isometric set, then recoloured onto the
     brand ramp and cropped to their subjects (scratchpad/rc3). The dark tile is
     theirs — it sits as a card on the paper ground. */
  items: [
    { k: "01", t: "Plotted layouts", d: "Ten to fifty acres, laid out to DTCP. A buyer reads the grid, the facing and the plot they are actually buying.", img: "/images/brand/types/plotted.svg?v=3" },
    { k: "02", t: "Villas", d: "The same scale of layout, with the home built and handed over. Every unit type walked room by room, on its own street.", img: "/images/brand/types/villas.svg?v=3" },
    { k: "03", t: "High rise", d: "Floor against floor. A buyer compares the fourteenth with the fortieth without climbing either of them.", img: "/images/brand/types/highrise.svg?v=3" },
    { k: "04", t: "Commercial", d: "Floor plates, frontage and the approach, for tenants and investors who will never visit the site twice.", img: "/images/brand/types/commercial.svg?v=3" },
    { k: "05", t: "Estates", d: "Fifty acres to three hundred. No plot grid: signature zones held inside landscape that only reads from the air.", img: "/images/brand/types/estates.svg?v=3" },
  ],
  ask: {
    t: "Something else?",
    d: "Hospitality, plantation, a township, a redevelopment. If it can be captured, it can be walked — tell us what you are building.",
    cta: "Talk to us →",
  },


};

/* ── the theatre ─────────────────────────────────────────────────────────
   The eleven live capability blocks, named exactly as they appear on every
   client proposal (engagements repo, shared/platform.mjs) so a builder who
   has seen both recognises them. */
export const platform = {
  eyebrow: "The platform",
  headline: "A walkthrough nobody sees is worth nothing.",
  said: "— so most of what we build is everything around it.",
  body: "Everything here exists to get the walkthrough in front of a buyer, prove who sent it, and tell you what happened afterwards. Eleven today, and the list grows.",
  features: [
    { k: "Walkthrough analytics", d: "Dwell time, space by space. Which rooms held a buyer, and which they walked past.", icon: "bars" },
    { k: "Share it, and know who shared it", d: "Every link carries the agent who sent it, so the lead comes back attributed.", icon: "share" },
    { k: "Live on your own website", d: "One snippet puts the walkthrough inside your own site, gated for leads.", icon: "embed" },
    { k: "Leads land in your own CRM", d: "Sell.do, LeadSquared, Salesforce — the lead arrives where your team already works.", icon: "crm" },
    { k: "Pitch it live, together", d: "Your agent hosts the walkthrough while the buyer follows from another city.", icon: "guided" },
    { k: "Live inventory, in the same link", d: "What is sold and what is left, inside the experience rather than beside it.", icon: "inventory" },
    { k: "One home, in focus", d: "A private link to a single unit, for the buyer who is down to one.", icon: "focus" },
    { k: "Where the demand actually is", d: "Which cities and which sources your buyers are opening from.", icon: "map" },
    { k: "Three tiers, one view", d: "Project, tower and unit in one navigation a buyer never has to think about.", icon: "tiers" },
    { k: "Offline mode", d: "It works at the site office on a bad connection, which is where it is needed most.", icon: "offline" },
    { k: "A host that never sleeps", d: "An AI presenter that walks a buyer through at two in the morning.", icon: "ai" },
  ],
};

/* ── who it is for ───────────────────────────────────────────────────────
   Four audiences, one page. Separate pages are a sales decision, not a design
   one — build them when an audience converts badly enough to need a longer
   argument. */
export const audiences = {
  eyebrow: "Who it's for",
  headline: "Four ways in.",
  items: [
    { k: "01", t: "Builders", d: "Put every unit on a link your team can send, and watch what buyers do inside it." },
    { k: "02", t: "Channel partners", d: "Send a project under your own name and keep the lead attributed to you." },
    { k: "03", t: "Aggregate channel partners", d: "One view across every agent below you — who sent what, and what came back." },
    { k: "04", t: "Marketing agencies", d: "Embed the walkthrough in the campaign and capture leads at the moment of interest." },
  ],
};

export const how = {
  eyebrow: "How it works",
  headline: "Drawings in. A link out.",
  steps: [
    { n: "01", t: "You send what you have", d: "Plans, elevations, render sets, a site layout. Whatever exists today." },
    { n: "02", t: "We build the walkthrough", d: "Typically a week for a first project. You review it before anyone outside sees it." },
    { n: "03", t: "Your team sends links", d: "Each agent gets their own attributed link, and the platform does the rest." },
  ],
};

export const close = {
  eyebrow: "Start anywhere",
  headline: "One project. One link. One week.",
  body: "We build the first walkthrough from the drawings you already have, and you send it to a real buyer before deciding anything else.",
  primary: "Talk to us",
  /* TODO(hari): confirm this inbox exists. */
  email: "hello@openskyvr.com",
};

export const footer = {
  sig: "Walkthroughs, and the platform that gets them watched. Built in Hyderabad.",
  columns: [
    { h: "The walkthrough", items: film.scenes.map((s) => s.k) },
    { h: "The platform", items: ["Analytics", "Attribution", "Website embed", "CRM delivery", "Guided sessions"] },
    { h: "Who it's for", items: audiences.items.map((a) => a.t) },
  ],
  legal: ["Privacy policy", "Terms of use"],
};
